import React, { useState, useRef, useEffect } from 'react';
import { View, Text, ImageBackground, TextInput, TouchableOpacity, Animated, Platform, Easing, ActivityIndicator } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useNavigation } from '@react-navigation/native';
import { getAuth } from 'firebase/auth';
import { globalStyles } from '../styles/globalStyles';
import { homeStyles } from '../styles/HomeStyles';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { useAnimation } from '../hooks/useAnimation';
import { colors, shadows } from '../styles/theme';
import { SearchBar } from '../components/SearchBar';
import { useTypingAnimation } from '../hooks/useTypingAnimation';
import { useUser } from '../components/LoginPersistence';
import { useTheme } from '../contexts/ThemeContext';
import { canvaHomePageBg, canvaHomeTaglineColor } from '../utils/hamburgerMenu';

const PLACEHOLDER_PHRASES = [
  "Chicago",
  "M32",
  "Laundry",
  "The Independent",
  "Pinball",
  "Floor Stage",
  "Restaurant",
  "ChamSys",
  "Showers",
  "Philadelphia",
  "L-Acoustics",
  "The Garage",
  "Dressing Rooms"
];



export function Home() { 
  const navigation = useNavigation();
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const { theme, isDarkMode } = useTheme();
  const [searchText, setSearchText] = useState('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [animationsComplete, setAnimationsComplete] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const animatedPlaceholder = useTypingAnimation(PLACEHOLDER_PHRASES);
  const { user } = useUser();
  const isAdmin = user?.userType === 'admin';

  // Create refs for animations
  const titleFadeRef = useRef(new Animated.Value(0));
  const titleSlideRef = useRef(new Animated.Value(30));
  const subtitleFadeRef = useRef(new Animated.Value(0));
  const subtitleSlideRef = useRef(new Animated.Value(30));
  const buttonsFadeRef = useRef(new Animated.Value(0));
  const buttonsSlideRef = useRef(new Animated.Value(30));
  const searchFadeRef = useRef(new Animated.Value(0));
  const searchSlideRef = useRef(new Animated.Value(30));
  const toggleSlideRef = useRef(new Animated.Value(30));
  const toggleFadeRef = useRef(new Animated.Value(0));
  const activeButtonPositionRef = useRef(new Animated.Value(0)); // 0 = left (Venues), 1 = right (Community)

  useEffect(() => {
    // Animation configuration
    const DURATION = 400;
    const SEQUENCE_DELAY = 100;

    const animations = [
      // Title animations (first)
      Animated.parallel([
        Animated.timing(titleFadeRef.current, {
          toValue: 1,
          duration: DURATION,
          delay: 0,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        }),
        Animated.timing(titleSlideRef.current, {
          toValue: 0,
          duration: DURATION,
          delay: 0,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        })
      ]),
      // Subtitle animations
      Animated.parallel([
        Animated.timing(subtitleFadeRef.current, {
          toValue: 1,
          duration: DURATION,
          delay: SEQUENCE_DELAY,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        }),
        Animated.timing(subtitleSlideRef.current, {
          toValue: 0,
          duration: DURATION,
          delay: SEQUENCE_DELAY,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        })
      ]),
      // Search input animations
      Animated.parallel([
        Animated.timing(searchFadeRef.current, {
          toValue: 1,
          duration: DURATION,
          delay: SEQUENCE_DELAY,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        }),
        Animated.timing(searchSlideRef.current, {
          toValue: 0,
          duration: DURATION,
          delay: SEQUENCE_DELAY,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        })
      ]),
      // Buttons animations (last)
      Animated.parallel([
        Animated.timing(buttonsFadeRef.current, {
          toValue: 1,
          duration: DURATION,
          delay: SEQUENCE_DELAY,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        }),
        Animated.timing(buttonsSlideRef.current, {
          toValue: 0,
          duration: DURATION,
          delay: SEQUENCE_DELAY,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        })
      ]),
      // Toggle animations
      Animated.parallel([
        Animated.timing(toggleFadeRef.current, {
          toValue: 1,
          duration: DURATION,
          delay: SEQUENCE_DELAY,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        }),
        Animated.timing(toggleSlideRef.current, {
          toValue: 0,
          duration: DURATION,
          delay: SEQUENCE_DELAY,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        })
      ])
    ];

    // Start the sequence
    Animated.sequence(animations).start(() => {
      setAnimationsComplete(true);
    });
  }, []);

  const handleSearch = () => {
    if (searchText.trim()) {
      // Create a structured search object with the query and search timestamp
      navigation.navigate('VenueList', { 
        searchQuery: searchText,
        timestamp: Date.now() // Add timestamp to ensure navigation triggers even with same search term
      });
    }
  };

  const handleToggleToCommunity = () => {
    if (isTransitioning) return;
    // Always navigate; CommunityScreen will show a friendly "Coming Soon" popup if not admin

    setIsTransitioning(true);
    console.log('🎯 Starting toggle animation: Venues → Community');
    console.log('🎯 Current activeButtonPosition:', activeButtonPositionRef.current._value);
    
    // Step 1: Animate the blue button sliding from Venues (left) to Community (right)
    Animated.timing(activeButtonPositionRef.current, {
      toValue: 1, // Move to right position (Community)
      duration: 400,
      easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), // Smooth ease-out
      useNativeDriver: false, // We need to animate layout properties
    }).start(() => {
      console.log('🎯 Blue indicator slide completed, starting fade down');
      console.log('🎯 Final activeButtonPosition:', activeButtonPositionRef.current._value);
      // Step 2: After button animation completes, fade everything down
      Animated.parallel([
        Animated.timing(toggleFadeRef.current, {
          toValue: 0,
          duration: 300,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        }),
        Animated.timing(toggleSlideRef.current, {
          toValue: 30,
          duration: 300,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        }),
        Animated.timing(subtitleFadeRef.current, {
          toValue: 0,
          duration: 250,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        }),
        Animated.timing(subtitleSlideRef.current, {
          toValue: 30,
          duration: 250,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        }),
        Animated.timing(searchFadeRef.current, {
          toValue: 0,
          duration: 250,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        }),
        Animated.timing(searchSlideRef.current, {
          toValue: 30,
          duration: 250,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        })
      ]).start(() => {
        // Step 3: Navigate to CommunityScreen after fade animation completes
        navigation.replace('CommunityScreen');
      });
    });
  };

  const handleNearMe = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setIsGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}&localityLanguage=en`
          );
          const data = await response.json();
          
          // Format the location string properly
          const city = data.city || data.locality || '';
          const state = data.principalSubdivision || '';
          const searchString = `${city}, ${state}`.trim();
          
          setSearchText(searchString);
          
          navigation.navigate('VenueList', { 
            searchQuery: searchString,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            nearMe: true
          });
        } catch (error) {
          console.error('Error getting location details:', error);
          alert('Error getting your location details. Please try again.');
        }
        setIsGettingLocation(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        alert('Error accessing location. Please allow location access and try again.');
        setIsGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );
  };

  return (
    <View style={[globalStyles.container, { backgroundColor: canvaHomePageBg(isDarkMode) }]}>
      {isDarkMode ? <BackgroundVideo /> : null}
      {/* <ImageBackground
        source={require('../../assets/backgrounds/background.jpg')}
        style={globalStyles.backgroundImage}
        resizeMode="cover"
      > */}
        <Animated.View 
          style={[
            globalStyles.overlay,
            { 
              opacity: 1,
              paddingTop: 0,
              zIndex: 1,
              position: 'relative',
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
              backgroundColor: canvaHomePageBg(isDarkMode),
              backdropFilter: 'none',
            }
          ]}
        >
          <Animated.Text
            style={[
              homeStyles.homeSubtitle2,
              {
                position: 'absolute',
                top: 16,
                left: 0,
                right: 0,
                color: canvaHomeTaglineColor(isDarkMode),
                textAlign: 'center',
                width: '100%',
                zIndex: 3,
                opacity: titleFadeRef.current,
                transform: [{ translateY: titleSlideRef.current }],
              },
              Platform.OS === 'web' && {
                transition: 'transform 0.3s ease-in-out',
              }
            ]}
          >
            A Crowdsourced Database
          </Animated.Text>

          <View style={[
            homeStyles.homeContent, 
            { 
              position: 'relative', 
              zIndex: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              maxWidth: 1200,
              paddingBottom: 150,
            }
          ]}>
            <View style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Animated.View 
                style={[
                  homeStyles.searchContainer,
                  {
                    opacity: searchFadeRef.current,
                    transform: [{ translateY: searchSlideRef.current }],
                  }
                ]}
              >
                <SearchBar
                  color={theme.text.primary}
                  value={searchText}
                  onChangeText={setSearchText}
                  onSubmit={handleSearch}
                  placeholder={animatedPlaceholder}
                />
                <TouchableOpacity 
                  style={homeStyles.locationIconContainer}
                  onPress={handleNearMe}
                  disabled={isGettingLocation}
                >
                  {isGettingLocation ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <View style={homeStyles.svgIcon}>
                      <View style={[homeStyles.locationDot, { backgroundColor: isDarkMode ? '#ffffff' : '#000000' }]} />
                      <View style={[homeStyles.locationRing, { borderColor: isDarkMode ? '#ffffff' : '#000000' }]} />
                    </View>
                  )}
                </TouchableOpacity>
              </Animated.View>

              {/* Page Toggle — temporarily hidden */}
              {false && isAdmin && (
              <Animated.View
                style={[
                  {
                    opacity: toggleFadeRef.current || buttonsFadeRef.current,
                    transform: [
                      { translateY: toggleSlideRef.current }
                    ],
                    marginTop: 40,
                    backgroundColor: '#ffffff',
                    borderRadius: 25,
                    padding: 3,
                    borderWidth: 2,
                    borderColor: '#e0e0e0',
                    shadowColor: '#000',
                    shadowOffset: {
                      width: 0,
                      height: 4,
                    },
                    shadowOpacity: 0.15,
                    shadowRadius: 8,
                    elevation: 8,
                    position: 'relative',
                    width: 280,
                    height: 50,
                  }
                ]}
              >
                {/* Animated Blue Background */}
                <Animated.View
                  style={[
                    {
                      position: 'absolute',
                      top: 1,
                      width: 134, // Half of 280 - 6 (padding)
                      height: 44,
                      backgroundColor: '#4e7de0',
                      borderRadius: 22,
                      shadowColor: '#4e7de0',
                      shadowOffset: {
                        width: 0,
                        height: 2,
                      },
                      shadowOpacity: 0.3,
                      shadowRadius: 4,
                      elevation: 3,
                      left: activeButtonPositionRef.current.interpolate({
                        inputRange: [0, 1],
                        outputRange: [3, 143], // 3px to 143px (3 + 140)
                      }),
                    }
                  ]}
                />
                
                {/* Button Labels */}
                <View style={{
                  flexDirection: 'row',
                  flex: 1,
                }}>
                  <TouchableOpacity
                    style={{
                      flex: 1,
                      paddingHorizontal: 20,
                      paddingVertical: 12,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    disabled={isTransitioning}
                  >
                    <Animated.Text style={{
                      fontSize: 16,
                      fontFamily: 'VastagoGrotesk',
                      fontWeight: '600',
                      color: activeButtonPositionRef.current.interpolate({
                        inputRange: [0, 1],
                        outputRange: ['#ffffff', '#6c757d'], // White when active, gray when inactive
                      }),
                    }}>
                      Venues
                    </Animated.Text>
                  </TouchableOpacity>
                  
                  <TouchableOpacity
                    style={{
                      flex: 1,
                      paddingHorizontal: 20,
                      paddingVertical: 12,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    onPress={handleToggleToCommunity}
                    disabled={isTransitioning}
                  >
                    <Animated.Text style={{
                      fontSize: 16,
                      fontFamily: 'VastagoGrotesk',
                      fontWeight: '600',
                      color: activeButtonPositionRef.current.interpolate({
                        inputRange: [0, 1],
                        outputRange: ['#6c757d', '#ffffff'], // Gray when inactive, white when active
                      }),
                    }}>
                      Community
                    </Animated.Text>
                  </TouchableOpacity>
                </View>
              </Animated.View>
              )}
            </View>
          </View>
          <StatusBar style="light" />
        </Animated.View>
      {/* </ImageBackground> */}
    </View>
  );
}

